<svelte:options customElement={{ tag: "bt-curation-assign-modal" }} />

<script lang="ts">
  // Imports
  import { modalStore } from '$shared/modal/modalStore';
  import { onMount } from 'svelte';
  import Modal from '$shared/modal/Modal.svelte';
  import Icon from '$shared/icons/Icon.svelte';
  import { eventBus } from '$shared/lib/eventBus';

  // Props / types
  export let publishedState: string = 'false';
  export let groups: string = '[]';
  export let assignUrl: string = '';
  export let copyUrl: string = '';
  export let showEmailToggle: string = 'false';
  $: showEmailToggleBool = showEmailToggle === 'true';
  export let showSetRemindersButton: string = 'false';
  $: showSetRemindersButtonBool = showSetRemindersButton === 'true';

  // Translations
  // Email toggle translations
  export let sendEmailToggleLabel: string = 'Send email on Assign';
  export let sendEmailToggleDescription: string = 'Everyone in this group will be invited to start this curation.';
  export let sendEmailToggleLinkPrefix: string = 'Preview the email on our';
  export let sendEmailToggleLinkText: string = 'help center';

  export let assignButtonText: string = 'Assign';
  export let assignFootnote: string = 'Before confirming, double check that your curation is complete.';
  export let assignedTo: string = 'Assigned to: %{group_name}';
  export let assignedToLabel: string = 'Assigned to:';
  export let cancelButtonText: string = 'Cancel';
  export let due: string = 'Due';
  export let dueDatePlaceholder: string = 'Pick a date';
  export let dueText: string = 'Set a due date';
  export let noGroupsText: string = 'No groups available yet. Create a group first, then come back to assign this curation.';
  export let optionalText: string = 'Optional';
  export let publishedText: string = 'This curation is published. Unpublish it before assigning.';
  export let requiredText: string = 'Required';
  export let selectDropdownText: string = 'Select from dropdown';
  export let selectGroupText: string = 'Select a group';

  // Confirmation / error modal translations
  export let assignedModalTitle: string = 'Your curation is assigned';
  export let dueDateLabel: string = 'Due date:';
  export let noDueDateText: string = 'No due date';
  export let assignedEmailInvitedText: string = 'Everyone in this group has been invited to start this curation.';
  export let remindersCalloutText: string = 'Set email reminders to engage your learners, or copy the link to send it directly.';
  export let setRemindersText: string = 'Set reminders';
  export let copyLinkText: string = 'Copy link to curation';
  export let copiedText: string = 'Copied';
  export let returnToCurationText: string = 'Return to curation';
  export let invalidEmailsLabel: string = 'invalid emails';
  export let invalidEmailLabel: string = 'invalid email';
  export let invalidEmailPrefix: string = 'Your curation is successfully assigned, but';
  export let invalidEmailSuffix: string = 'have invalid email addresses and were not notified. Visit our';
  export let invalidEmailSuffixSingular: string = 'has an invalid email address and was not notified. Visit our';
  export let invalidEmailHelpText: string = 'help center';
  export let invalidEmailPostfix: string = 'to learn how you can update or add valid email addresses.';

  type Group = { id: number; name: string };

  // Constants
  const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') ?? '';

  // Local state / refs
  let selectedGroupId = '';
  let dueDate = '';
  let isGroupOpen = false;

  let dateInput: HTMLInputElement | null = null;

  let isSubmitting = false;
  let sendEmailOnAssign = false;
  let assignedGroupName = '';
  let assignedDueDateDisplay = '';
  let linkCopied = false;
  let linkCopyTimeout: number | undefined;
  let invalidEmailCount = 0;

  // Helpers
  const formatDueDate = (iso: string) => {
    const [y, m, d] = iso.split('-').map(Number);
    if (!y || !m || !d) return '';
    const date = new Date(y, m - 1, d);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const updateAssignmentBadges = (groupName?: string, dueDateIso?: string | null) => {
    const el = document.getElementById('curation-assignment-badges');
    if (!el || !groupName) return;

    const dueText = dueDateIso ? `${due} ${formatDueDate(dueDateIso)}` : null;

    el.innerHTML = `
      <bt-badge design="assign" text="${assignedTo.replace('%{group_name}', groupName)}"></bt-badge>
      ${dueText ? `<bt-badge design="assign" text="${dueText}"></bt-badge>` : ''}
    `;
  };

  const resetForm = () => {
    selectedGroupId = '';
    dueDate = '';
    isGroupOpen = false;
    isSubmitting = false;
    sendEmailOnAssign = false;
    assignedGroupName = '';
    assignedDueDateDisplay = '';
    linkCopied = false;
    invalidEmailCount = 0;
  };

  const closeModal = () => {
    resetForm();
    modalStore.setName('');
    modalStore.setData({});
  };

  const openReminders = () => {
    resetForm();
    modalStore.setName('curationReminders');
  };

  // Derived state
  $: modalName = $modalStore.modalName;
  $: publishedBool = publishedState === 'true';

  $: parsedGroups = (() => {
    try { return JSON.parse(groups || '[]') as Group[]; }
    catch { return []; }
  })();

  $: hasSelectedGroup = selectedGroupId !== '';
  $: selectedGroup = parsedGroups.find((g) => String(g.id) === selectedGroupId);
  $: dueDateDisplay = dueDate ? formatDueDate(dueDate) : '';

  // Actions / handlers
  const toggleGroupMenu = () => (isGroupOpen = !isGroupOpen);

  const selectGroup = (id: number) => {
    selectedGroupId = String(id);
    isGroupOpen = false;
  };

  const copyLink = async () => {
    if (!copyUrl) return;
    try {
      await navigator.clipboard.writeText(copyUrl);
      linkCopied = true;
      if (linkCopyTimeout) window.clearTimeout(linkCopyTimeout);
      linkCopyTimeout = window.setTimeout(() => { linkCopied = false; }, 2000);
    } catch (e) {
      console.error('Failed to copy link', e);
    }
  };

  const submitAssign = async () => {
    if (!assignUrl) {
      return;
    }
    if (!hasSelectedGroup) return;

    isSubmitting = true;
    try {
      const resp = await fetch(assignUrl, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
          'Accept': 'application/json',
        },
        body: JSON.stringify({
          group_id: Number(selectedGroupId),
          due_date: dueDate || null,
          send_email_on_assign: sendEmailOnAssign,
        }),
        credentials: 'same-origin',
      });

      if (!resp.ok) {
        return;
      }

      // Capture for confirmation modal
      const data = await resp.json().catch(() => ({}));
      invalidEmailCount = data.invalid_email_count ?? 0;
      assignedGroupName = selectedGroup?.name ?? '';
      assignedDueDateDisplay = dueDate ? formatDueDate(dueDate) : '';

      updateAssignmentBadges(selectedGroup?.name, dueDate || null);

      // Show reminders button when curation is assigned
      const remindersBtn = document.getElementById('curation-reminders-button');
      if (remindersBtn) remindersBtn.removeAttribute('hidden');

      // Keep the reminders panel's recipients current without a reload
      eventBus.emit('curation-assigned', { groupName: selectedGroup?.name ?? '' });
      if (sendEmailOnAssign) {
        eventBus.emit('curation-assigned-email-sent', { groupName: selectedGroup?.name ?? '' });
      }

      const share = document.querySelector('bt-curation-share-modal');
      if (share) {
        share.setAttribute('assignedgroupid', selectedGroupId);
        share.setAttribute('assignedduedate', dueDate || '');
        // @ts-ignore
        share.assignedgroupid = selectedGroupId;
        // @ts-ignore
        share.assignedduedate = dueDate || '';
      }

      // Show confirmation or error modal
      if (sendEmailOnAssign && invalidEmailCount > 0) {
        modalStore.setName('assignedCurationError');
      } else {
        modalStore.setName('assignedCuration');
      }
    } catch (e) {
      console.error(e);
    } finally {
      isSubmitting = false;
    }
  };

  // Lifecycle
  onMount(() => {
    let prevName = '';

    const unsubscribe = modalStore.subscribe((currentModal: any) => {
      const nextName = currentModal?.modalName ?? '';

      if (nextName === 'assignCuration') {
        document.querySelectorAll('.assign-preview-slot').forEach(slotEl => {
          slotEl.removeAttribute('hidden');
        });
      }

      if (prevName === 'assignCuration' && nextName !== 'assignCuration' && nextName !== 'assignedCuration' && nextName !== 'assignedCurationError') {
        resetForm();
      }
      prevName = nextName;
    });
    return unsubscribe;
  });
</script>

{#if modalName === 'assignedCuration'}
  <Modal modalTitle={assignedModalTitle} showCloseIcon={true} showFooter={false} width="medium">
    <div slot="modal-contents" class="assigned-body">
      <p class="assigned-row"><span class="assigned-row__label">{assignedToLabel}</span> {assignedGroupName}</p>
      <p class="assigned-row"><span class="assigned-row__label">{dueDateLabel}</span> {assignedDueDateDisplay || noDueDateText}</p>
      {#if sendEmailOnAssign}
        <p class="assigned-row">{assignedEmailInvitedText}</p>
      {/if}
      {#if showSetRemindersButtonBool}
        <div class="reminders-callout">
          <span class="reminders-callout__icon"><Icon icon="Info" size="medium" /></span>
          <span>{remindersCalloutText}</span>
        </div>
      {/if}
      <div class="assigned-ctas">
        <button type="button" class="cta-outline" on:click={copyLink}>
          <Icon icon="Link" size="small" />
          {#if linkCopied}<Icon icon="Check" size="small" />{copiedText}{:else}{copyLinkText}{/if}
        </button>
        {#if showSetRemindersButtonBool}
          <button type="button" class="cta-filled" on:click={openReminders}>
            {setRemindersText}
          </button>
        {:else}
          <button type="button" class="cta-filled" on:click={closeModal}>
            {returnToCurationText}
          </button>
        {/if}
      </div>
    </div>
  </Modal>
{/if}

{#if modalName === 'assignedCurationError'}
  <Modal modalTitle={assignedModalTitle} showCloseIcon={true} showFooter={false} width="medium">
    <div slot="modal-contents" class="assigned-body">
      <div class="email-error-alert">
        <div class="email-error-alert__header">
          <span style="display: flex; color: #EF4444"><Icon icon="CircleX" size="medium" /></span>
          <span>{invalidEmailCount} {invalidEmailCount === 1 ? invalidEmailLabel : invalidEmailsLabel}</span>
        </div>
      </div>
      <p class="assigned-row">{invalidEmailPrefix} {invalidEmailCount} learner{invalidEmailCount === 1 ? '' : 's'} {invalidEmailCount === 1 ? invalidEmailSuffixSingular : invalidEmailSuffix} <a href="https://help.bigthink.com/help/share-a-curation" target="_blank" class="toggle-link">{invalidEmailHelpText}</a> {invalidEmailPostfix}</p>
      <div class="assigned-ctas">
        <button type="button" class="cta-outline" on:click={copyLink}>
          <Icon icon="Link" size="small" />
          {#if linkCopied}<Icon icon="Check" size="small" />{copiedText}{:else}{copyLinkText}{/if}
        </button>
        <button type="button" class="cta-filled" on:click={closeModal}>
          {returnToCurationText}
        </button>
      </div>
    </div>
  </Modal>
{/if}

{#if modalName === 'assignCuration'}
  <Modal modalTitle="Assign curation" width="wide" showCloseIcon={true} showFooter={false} closeButtonClasses="modal__close-x--assign">
    <div slot="modal-contents" class="assign-modal">
      <div class="assign-card">
        <slot name="contents"></slot>
      </div>
      <div class="assign-right-column">
        {#if publishedBool}
          <p>{publishedText}</p>
        {:else}
          <div class="assign-right-column-top">
            <!-- Group select -->
            <div class="assign-field">
              <div class="assign-label-stack">
                <div class="assign-label">{selectGroupText}</div>
                <div class="assign-meta">{requiredText}</div>
              </div>
              {#if parsedGroups.length === 0}
                <p class="assign-empty">{noGroupsText}</p>
              {:else}
              <div class="assign-select">
                <button
                  type="button"
                  class="assign-select__button {hasSelectedGroup ? '' : 'is-placeholder'}"
                  on:click={toggleGroupMenu}
                >
                  <span class="assign-select__text">
                    {#if !hasSelectedGroup}
                      {selectDropdownText}
                    {:else}
                      {selectedGroup?.name}
                    {/if}
                  </span>
                  <span class="assign-select__icon">
                    <Icon icon="ChevronDown" size="small" />
                  </span>
                </button>
                {#if isGroupOpen}
                  <ul class="assign-select__menu" on:blur={toggleGroupMenu}>
                    {#each parsedGroups as group (group.id)}
                      <li class="assign-select__item">
                        <button
                          type="button"
                          class="assign-select__option {String(group.id) === selectedGroupId ? 'is-selected' : ''}"
                          on:click|stopPropagation={() => selectGroup(group.id)}
                        >
                          {group.name}
                        </button>
                      </li>
                    {/each}
                  </ul>
                {/if}
              </div>
              {/if}
            </div>

            <!-- Due date -->
            <div class="assign-field">
              <div class="assign-label-stack">
                <div class="assign-label">{dueText}</div>
                <div class="assign-meta">{optionalText}</div>
              </div>
              <div
                class="assign-date {dueDate ? 'has-value' : 'is-empty'}"
                role="button"
                tabindex="0"
                on:click={() => {
                  if (dateInput?.showPicker) dateInput.showPicker();
                  else dateInput?.focus();
                }}
                on:keydown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    dateInput?.showPicker?.();
                  }
                }}
              >
                <span class="assign-date__icon">
                  <Icon icon="Calendar" size="small" />
                </span>
                {#if !dueDate}
                  <span class="assign-date__placeholder">{dueDatePlaceholder}</span>
                {:else}
                  <span class="assign-date__value">{dueDateDisplay}</span>
                {/if}
                <input bind:this={dateInput} class="assign-control--date" type="date" bind:value={dueDate} aria-label={dueDatePlaceholder}/>
              </div>
            </div>

            <!-- Email toggle -->
            {#if showEmailToggleBool}
            <div class="email-toggle-container">
              <label class="toggle-row">
                <input
                  type="checkbox"
                  class="toggle-input"
                  bind:checked={sendEmailOnAssign}
                />
                <span class="toggle-label">{sendEmailToggleLabel}</span>
              </label>
              <p class="toggle-description">
                {sendEmailToggleDescription}
                {sendEmailToggleLinkPrefix} <a href="https://help.bigthink.com/help/share-a-curation" target="_blank" class="toggle-link">{sendEmailToggleLinkText}</a>.
              </p>
            </div>
            {/if}
          </div>
        {/if}

        <!-- Buttons always visible; Assign disabled until group selected -->
        <div class="assign-actions">
          <button
            type="button"
            class="assign-cancel"
            on:click={closeModal}>
            {cancelButtonText}
          </button>
          <button
            type="button"
            class="assign-confirm"
            disabled={isSubmitting || !hasSelectedGroup}
            on:click={submitAssign}>
            {assignButtonText}
          </button>
        </div>
      </div>
    </div>
  </Modal>
{/if}
<style>
  .assign-modal {
    display: flex;
    flex-direction: row;
    gap: 24px;
    @media (max-width: theme('screens.sm')) {
      flex-direction: column;
      max-height: calc(100vh - 8rem);
      overflow-y: auto;
    }
  }
  .assign-card {
    width: 327px;
    height: 446px;
    flex-shrink: 0;
    overflow: hidden;
    @media (max-width: theme('screens.sm')) {
      width: 100%;
    }
  }
  .assign-right-column {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    flex: 1;
    min-width: 0;
  }
  .assign-right-column-top {
    display: flex;
    flex-direction: column;
    gap: 24px;
  }
  .assign-field {
    display: flex;
    flex-direction: column;
    gap: theme('spacing.2');
    min-width: 0;
  }
  .assign-label-stack {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
  }
  .assign-label {
    font-weight: 500;
    font-size: theme('fontSize.base');
    line-height: theme('lineHeight.normal');
  }
  .assign-meta {
    font-weight: 400;
    font-size: theme('fontSize.xs');
    color: theme('colors.neutral.500');
    opacity: 0.65;
  }
  .assign-empty {
    margin: 0;
    opacity: 0.75;
  }
  /* ---------- Group Select ---------- */
  .assign-select {
    position: relative;
    width: 100%;
  }
  .assign-select__button {
    width: 100%;
    height: 56px;
    padding: 0 16px;
    border: 1px solid theme('colors.neutral.300');
    border-radius: theme('borderRadius.lg');
    background: theme('colors.white');
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: theme('spacing.3');
    cursor: pointer;
    font-family: theme('fontFamily.sans');
  }
  .assign-select__text {
    flex: 1;
    min-width: 0;
    font-size: theme('fontSize.base');
    font-weight: 400;
    color: theme('colors.black');
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    text-align: left;
  }
  .assign-select__option {
    font-size: theme('fontSize.base');
  }
  .assign-select__button.is-placeholder .assign-select__text {
    color: theme('colors.neutral.500');
  }
  .assign-select__icon {
    flex-shrink: 0;
    pointer-events: none;
    display: flex;
    align-items: center;
  }
  .assign-select__menu {
    position: absolute;
    left: 0;
    top: calc(100% + theme('spacing.2'));
    width: 100%;
    background: theme('colors.white');
    border: 1px solid theme('colors.neutral.300');
    border-radius: theme('borderRadius.xl');
    padding: theme('spacing.2');
    margin: 0;
    list-style: none;
    z-index: 50;
    max-height: 280px;
    overflow: auto;
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.12);
  }
  .assign-select__item {
    list-style: none;
  }
  .assign-select__option {
    width: 100%;
    text-align: left;
    background: transparent;
    border: 0;
    padding: theme('spacing.3');
    border-radius: theme('borderRadius.lg');
    cursor: pointer;
    font-size: theme('fontSize.base');
    font-weight: 200;
    font-family: theme('fontFamily.sans');
    color: theme('colors.black');
  }
  .assign-select__option:hover,
  .assign-select__option:focus-visible {
    background: rgba(0, 0, 0, 0.06);
    outline: none;
  }
  .assign-select__option.is-selected {
    background: rgba(0, 0, 0, 0.06);
  }
  /* ---------- Due Date ---------- */
  .assign-date {
    position: relative;
    width: 100%;
    height: 56px;
    cursor: pointer;
  }
  .assign-date__icon,
  .assign-date__placeholder,
  .assign-date__value {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    pointer-events: none;
    z-index: 2;
  }
  .assign-date__icon {
    left: theme('spacing.4');
  }
  .assign-date__placeholder,
  .assign-date__value {
    left: theme('spacing.11');
    white-space: nowrap;
    overflow: hidden;
    text-overflow: clip;
    font-weight: 500;
    color: theme('colors.black');
  }
  .assign-control--date {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 56px;
    padding-left: theme('spacing.11');
    background: transparent;
    cursor: pointer;
    appearance: none;
    color: transparent;
    caret-color: transparent;
    padding: 0;
    border: 1px solid theme('colors.neutral.300');
    border-radius: theme('borderRadius.lg');
  }
  .assign-date.is-empty .assign-control--date {
    color: transparent;
    caret-color: transparent;
  }
  .assign-control--date::-webkit-calendar-picker-indicator {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    opacity: 0;
    cursor: pointer;
  }
  /* ---------- Actions ---------- */
  .assign-actions {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 24px;
  }
  .assign-cancel {
    background: transparent;
    border: none;
    padding: 2px;
    font-size: theme('fontSize.sm');
    font-weight: 500;
    font-family: theme('fontFamily.sans');
    color: theme('colors.black');
    cursor: pointer;
    &:hover { text-decoration: underline; }
  }
  .assign-confirm {
    background: theme('colors.black');
    color: theme('colors.white');
    border: none;
    padding: 10px 24px;
    border-radius: theme('borderRadius.lg');
    font-size: theme('fontSize.sm');
    font-family: theme('fontFamily.sans');
    font-weight: 500;
    cursor: pointer;
    &:hover:not(:disabled) { opacity: 0.9; }
    &:disabled { opacity: 0.4; cursor: not-allowed; }
  }

  /* ---------- Email Toggle ---------- */
  .email-toggle-container {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .toggle-row {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 8px;
    cursor: pointer;
  }

  .toggle-label {
    font-size: 16px;
    font-weight: 500;
    line-height: 24px;
    color: #000000;
  }

  .toggle-description {
    font-size: 16px;
    font-weight: 400;
    line-height: 24px;
    color: #737373;
    margin: 0;
  }

  .toggle-link {
    text-decoration: underline;
    color: #737373;
  }

  .toggle-input {
    appearance: none;
    -webkit-appearance: none;
    position: relative;
    flex-shrink: 0;
    width: 26px;
    height: 16px;
    margin: 0;
    border-radius: 51px;
    background: #D4D4D4;
    border: none;
    cursor: pointer;
    transition: background 0.2s;
    &::before {
      content: '';
      position: absolute;
      top: 1px;
      left: 1px;
      width: 14px;
      height: 14px;
      border-radius: 50%;
      background: #FFFFFF;
      box-shadow: 0px 1.5px 0.5px 0px rgba(0,0,0,0.06), 0px 1.5px 4px 0px rgba(0,0,0,0.15), 0px 0px 0px 0.5px rgba(0,0,0,0.04);
      transition: transform 0.2s;
    }
    &:checked {
      background: #1C9FFE;
      &::before {
        transform: translateX(10px);
      }
    }
  }

  /* ---------- Error Alert ---------- */
  .email-error-alert {
    background: #FEF2F2;
    border: 1px solid #FEE2E2;
    border-radius: 8px;
    padding: 16px;
  }

  .email-error-alert__header {
    display: flex;
    align-items: center;
    gap: 16px;
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
    color: #737373;
  }

  /* ---------- Assigned Confirmation Modal ---------- */
  .assigned-body {
    display: flex;
    flex-direction: column;
    gap: 24px;
  }

  .assigned-row {
    margin: 0;
    color: theme('colors.black');
    font-family: theme('fontFamily.sans');
    font-size: theme('fontSize.base');
    font-weight: 400;
    line-height: theme('lineHeight.6');
    @media (max-width: theme('screens.sm')) {
      font-size: theme('fontSize.sm');
      line-height: 21px;
    }
  }

  .assigned-row__label {
    font-weight: 500;
  }

  .assigned-ctas {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: theme('spacing.2');
    @media (max-width: theme('screens.sm')) {
      flex-direction: column;
      align-items: flex-end;
      gap: theme('spacing.4');
    }
  }

  /* Reminders info callout */
  .reminders-callout {
    display: flex;
    align-items: center;
    gap: theme('spacing.4');
    align-self: stretch;
    background: theme('colors.sky.50');
    border: 1px solid theme('colors.sky.100');
    border-radius: theme('borderRadius.lg');
    padding: theme('padding.4');
    font-size: theme('fontSize.sm');
    font-weight: 400;
    line-height: 21px;
    color: theme('colors.neutral.500');
  }

  .reminders-callout__icon {
    display: flex;
    color: theme('colors.sky.500');
  }

  .cta-outline {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    height: theme('spacing.10');
    padding: 0 theme('padding.4');
    background: theme('colors.white');
    border: 1px solid #D4D4D4;
    border-radius: theme('borderRadius.lg');
    font-weight: 500;
    font-size: theme('fontSize.sm');
    font-family: theme('fontFamily.sans');
    cursor: pointer;
    &:hover { background: theme('colors.neutral.100'); }
  }

  .cta-filled {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    height: theme('spacing.10');
    padding: 0 theme('padding.4');
    background: theme('colors.black');
    color: theme('colors.white');
    border: none;
    border-radius: theme('borderRadius.lg');
    font-weight: 500;
    font-size: theme('fontSize.sm');
    font-family: theme('fontFamily.sans');
    cursor: pointer;
    &:hover { background: theme('colors.neutral.800'); }
  }
</style>
